import { useEffect, useState } from "react";
import { masterApi, apiError, formatBRL, formatPhone } from "@/lib/api";
import { useUnit } from "@/pages/Master/MasterApp";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { modal } from "@/lib/modal";
import { rotuloTipoDesconto } from "@/lib/rotulos";
import TicketAltoFrentista from "@/pages/Master/TicketAltoFrentista";
import { TrendingDown, Target, Users2, DollarSign, Check, ShieldAlert } from "lucide-react";

const inputCls = "w-full h-9 rounded-lg bg-white border border-neutral-200 px-3 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition";

// 4.20: rótulos em lib/rotulos.js ('frequencia' do motor e tipos compostos '+aditivada').

export default function MasterAnalises() {
  const [tab, setTab] = useState("churn");
  return (
    <div className="px-4 md:px-6 pt-4 pb-8 space-y-4 float-in" data-testid="master-analises">
      <div>
        <div className="chip mb-1.5 text-[10px] py-0.5">Inteligência de negócio</div>
        <h1 className="font-serif-display text-2xl md:text-3xl leading-[1.1]">Análises</h1>
      </div>

      <div className="flex gap-1 p-1 bg-white/80 border border-neutral-200/70 rounded-full w-fit overflow-x-auto max-w-full">
        {[
          ["churn", "Churn", TrendingDown],
          ["eficacia", "Eficácia descontos", Target],
          ["funil", "Funil indicação", Users2],
          ["margem", "Margem & ROI", DollarSign],
          ["antifraude", "Anti-fraude & Retenção", ShieldAlert],
        ].map(([k, lbl, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`an-tab-${k}`}
                  className={`px-3 md:px-4 h-8 rounded-full text-[12px] font-medium transition-colors flex items-center gap-1.5 whitespace-nowrap ${
                    tab === k ? "bg-neutral-900 text-white" : "text-neutral-700 hover:bg-neutral-100"
                  }`}>
            <Icon size={12} /> {lbl}
          </button>
        ))}
      </div>

      {tab === "churn" && <Churn />}
      {tab === "eficacia" && <Eficacia />}
      {tab === "funil" && <Funil />}
      {tab === "margem" && <Margem />}
      {tab === "antifraude" && <AntiFraude />}
    </div>
  );
}

function AntiFraude() {
  const { unitId } = useUnit();
  const [freq, setFreq] = useState(null);
  const [suspeitas, setSuspeitas] = useState(null);
  const [conc, setConc] = useState(null);
  const [diasT, setDiasT] = useState(7);
  const [minErros, setMinErros] = useState(2);
  const [diasC, setDiasC] = useState(30);
  const [minPct, setMinPct] = useState(80);
  const [minAtd, setMinAtd] = useState(4);

  const load = async () => {
    const u = unitId !== "all" ? `&unit_id=${unitId}` : "";
    try {
      const [f, s, c] = await Promise.all([
        masterApi.get(`/master/analises/frequencia-clientes?dias_sumido=30`),
        masterApi.get(`/master/analises/tentativas-suspeitas?dias=${diasT}&min_erros=${minErros}${u}`),
        masterApi.get(`/master/analises/concentracao-vendedor?dias=${diasC}&min_pct=${minPct}&min_atendimentos=${minAtd}${u}`),
      ]);
      setFreq(f.data); setSuspeitas(s.data); setConc(c.data);
    } catch (e) { modal.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [unitId, diasT, minErros, diasC, minPct, minAtd]);

  return (
    <div className="space-y-4" data-testid="an-antifraude">
      {/* Retenção / frequência da base */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5">Frequência da base de clientes</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Kpi label="Total cadastros" value={freq ? String(freq.total) : "…"} />
          <Kpi label="1º cad. sem retorno" value={freq ? String(freq.sem_retorno) : "…"} accent="rose"
               sub="cadastrou e nunca abasteceu" />
          <Kpi label="Baixa frequência" value={freq ? String(freq.baixa_frequencia) : "…"} accent="amber"
               sub={`1 a ${freq ? freq.min_freq - 1 : 3} abast.`} />
          <Kpi label="Frequentes" value={freq ? String(freq.frequentes) : "…"} accent="emerald"
               sub={`≥ ${freq ? freq.min_freq : 4} abast.`} />
          <Kpi label="Sumidos (30d)" value={freq ? String(freq.sumidos) : "…"} accent="amber"
               sub="sem voltar há 30 dias" />
        </div>
      </div>

      {/* QUADRO 1: tentativas de 4 dígitos suspeitas */}
      <div className="card-soft rounded-xl p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 mr-auto">
            <ShieldAlert size={15} className="text-rose-600" />
            <h3 className="font-serif-display text-base">Tentativas de 4 dígitos suspeitas</h3>
          </div>
          <Stepper label="dias" value={diasT} onChange={setDiasT} />
          <Stepper label="mín. erros" value={minErros} onChange={setMinErros} />
        </div>
        <p className="text-[11px] text-neutral-500">
          Clientes que erraram os 4 últimos dígitos mais de {minErros}× nos últimos {diasT} dias — pode indicar
          uso de cadastro alheio ou telefone desatualizado.
        </p>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-neutral-50">
              <TableHead className="text-[10px] uppercase tracking-widest">Cliente</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Placa</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Telefone</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-center">Erros</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Última</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-center">Estado</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(suspeitas?.itens || []).map((r) => (
                <TableRow key={r.cliente_id} data-testid={`susp-${r.cliente_id}`}>
                  <TableCell className="font-medium">{r.nome}</TableCell>
                  <TableCell className="font-mono uppercase">{r.placa || "—"}</TableCell>
                  <TableCell>{formatPhone(r.telefone)}</TableCell>
                  <TableCell className="text-center font-semibold text-rose-600">{r.erros}</TableCell>
                  <TableCell className="text-xs">{r.ultima ? new Date(r.ultima).toLocaleString("pt-BR") : "—"}</TableCell>
                  <TableCell className="text-center">
                    {r.bloqueado
                      ? <span className="chip text-[10px] py-0.5" style={{ background: "rgba(225,29,72,0.15)", color: "#9F1239" }}>Bloqueado</span>
                      : <span className="text-[11px] text-neutral-500">ok</span>}
                  </TableCell>
                </TableRow>
              ))}
              {suspeitas && suspeitas.itens.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-neutral-500 py-5">Nenhuma tentativa suspeita no período. 👍</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* QUADRO 2: concentração em um único vendedor */}
      <div className="card-soft rounded-xl p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 mr-auto">
            <Users2 size={15} className="text-amber-600" />
            <h3 className="font-serif-display text-base">Concentração em um único vendedor</h3>
          </div>
          <Stepper label="dias" value={diasC} onChange={setDiasC} />
          <Stepper label="mín. atend." value={minAtd} onChange={setMinAtd} />
          <Stepper label="% mín." value={minPct} onChange={setMinPct} />
        </div>
        <p className="text-[11px] text-neutral-500">
          Clientes com ≥{minAtd} atendimentos em {diasC} dias cujos abastecimentos se concentram em <b>um mesmo frentista</b> acima
          de {minPct}% — padrão que pode indicar conluio para descontos indevidos.
        </p>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-neutral-50">
              <TableHead className="text-[10px] uppercase tracking-widest">Cliente</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Vendedor dominante</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-center">No vendedor</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-center">Total</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-center">%</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(conc?.itens || []).map((r) => (
                <TableRow key={r.cliente_id} data-testid={`conc-${r.cliente_id}`}>
                  <TableCell className="font-medium">{r.nome}</TableCell>
                  <TableCell>{r.vendedor}</TableCell>
                  <TableCell className="text-center">{r.no_vendedor}</TableCell>
                  <TableCell className="text-center">{r.total}</TableCell>
                  <TableCell className="text-center font-semibold text-amber-700">{r.pct}%</TableCell>
                </TableRow>
              ))}
              {conc && conc.itens.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-neutral-500 py-5">Nenhuma concentração acima do limite. 👍</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* QUADRO 3 (item 6, 19/10): frentista com ticket alto contra os colegas e o próprio histórico */}
      <TicketAltoFrentista />
    </div>
  );
}

function Stepper({ label, value, onChange }) {
  return (
    <div className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-full bg-neutral-50 border border-neutral-200">
      <span className="text-[9px] uppercase tracking-wider text-neutral-400">{label}</span>
      <input type="number" value={value} onChange={(e) => onChange(parseInt(e.target.value) || 0)}
             className="h-6 w-12 bg-transparent text-center text-xs font-semibold outline-none" />
    </div>
  );
}

function Kpi({ label, value, accent, sub }) {
  const bg = accent === "rose" ? "bg-gradient-to-br from-rose-50 to-rose-100"
    : accent === "emerald" ? "bg-gradient-to-br from-emerald-50 to-emerald-100"
    : accent === "amber" ? "bg-gradient-to-br from-amber-50 to-amber-100" : "bg-white";
  return (
    <div className={`card-soft rounded-xl p-3.5 ${bg}`}>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="font-serif-display text-xl mt-0.5 leading-tight">{value}</div>
      {sub && <div className="text-[10px] text-neutral-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Churn() {
  const { unitId } = useUnit();
  const [janela, setJanela] = useState(30);
  const [minA, setMinA] = useState(3);
  const [data, setData] = useState(null);
  const load = async () => {
    const params = new URLSearchParams({ janela_dias: String(janela), min_abast_anterior: String(minA) });
    if (unitId !== "all") params.set("unit_id", unitId);
    try {
      const r = await masterApi.get(`/master/analises/churn?${params}`);
      setData(r.data);
    } catch (e) { modal.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [unitId, janela, minA]);
  if (!data) return <div className="text-sm text-neutral-500">Carregando...</div>;

  return (
    <div className="space-y-3" data-testid="an-churn">
      <div className="card-soft rounded-xl p-3 flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Janela (dias)</label>
          <input type="number" data-testid="churn-janela" value={janela}
                 onChange={(e) => setJanela(parseInt(e.target.value) || 30)}
                 className={inputCls + " mt-1 w-24"} />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Min. abast. anterior</label>
          <input type="number" data-testid="churn-min" value={minA}
                 onChange={(e) => setMinA(parseInt(e.target.value) || 3)}
                 className={inputCls + " mt-1 w-24"} />
        </div>
        <div className="text-[11px] text-neutral-500 ml-auto">
          Abasteceram <b>≥{minA}x</b> nos {janela} dias anteriores e <b>0x</b> nos últimos {janela} dias.
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Kpi label="Clientes em churn" value={String(data.total)} accent="rose" />
        <Kpi label="Perda estimada" value={formatBRL(data.perda_estimada_total)} accent="rose"
             sub="soma do que gastaram no período anterior" />
        <Kpi label="Janela analisada" value={`${data.janela_dias} dias`} />
      </div>

      <div className="card-soft rounded-xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-50">
              <TableHead className="text-[10px] uppercase tracking-widest">Cliente</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Telefone</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Placa</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Abast. anterior</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Gasto anterior</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Sumido há</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Saudade?</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.slice(0, 200).map((c) => (
              <TableRow key={c.cliente_id} data-testid={`churn-row-${c.cliente_id}`}>
                <TableCell className="font-medium">{c.nome}</TableCell>
                <TableCell>{formatPhone(c.telefone)}</TableCell>
                <TableCell className="text-xs">{c.placa || "—"}</TableCell>
                <TableCell>{c.qty_anterior}</TableCell>
                <TableCell className="text-rose-700">{formatBRL(c.gasto_anterior)}</TableCell>
                <TableCell className="text-xs">{c.dias_sem_voltar != null ? `${c.dias_sem_voltar}d` : "—"}</TableCell>
                <TableCell className="text-xs">{c.cupom_saudade_ativo ? <Check size={15} className="inline text-emerald-600" /> : "—"}</TableCell>
              </TableRow>
            ))}
            {data.items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-neutral-500 py-6">Nenhum cliente em churn</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      {data.items.length > 200 && (
        <div className="text-[10px] text-neutral-500 px-1">Exibindo top 200 de {data.items.length}.</div>
      )}
    </div>
  );
}

function Eficacia() {
  const { unitId } = useUnit();
  const [dias, setDias] = useState(30);
  const [data, setData] = useState(null);
  const load = async () => {
    const params = new URLSearchParams({ dias: String(dias) });
    if (unitId !== "all") params.set("unit_id", unitId);
    try {
      const r = await masterApi.get(`/master/analises/eficacia-descontos?${params}`);
      setData(r.data);
    } catch (e) { modal.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [unitId, dias]);
  if (!data) return <div className="text-sm text-neutral-500">Carregando...</div>;

  return (
    <div className="space-y-3" data-testid="an-eficacia">
      <div className="card-soft rounded-xl p-3 flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Janela (dias)</label>
          <input type="number" data-testid="ef-dias" value={dias}
                 onChange={(e) => setDias(parseInt(e.target.value) || 30)}
                 className={inputCls + " mt-1 w-24"} />
        </div>
        <div className="text-[11px] text-neutral-500 ml-auto">
          Taxa de retorno = % dos clientes que receberam o desconto e voltaram nos {dias} dias seguintes.
        </div>
      </div>

      <div className="card-soft rounded-xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-50">
              <TableHead className="text-[10px] uppercase tracking-widest">Tipo</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Aplicações</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Clientes</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Litros movidos</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Total descontado</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Desc/aplicação</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Taxa retorno</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((r) => {
              const cor = r.taxa_retorno_pct >= 50 ? "text-emerald-700" : r.taxa_retorno_pct >= 25 ? "text-amber-700" : "text-rose-700";
              return (
                <TableRow key={r.tipo} data-testid={`ef-row-${r.tipo}`}>
                  <TableCell className="font-medium">{rotuloTipoDesconto(r.tipo)}</TableCell>
                  <TableCell>{r.aplicacoes}</TableCell>
                  <TableCell>{r.clientes_unicos}</TableCell>
                  <TableCell>{r.litros_movidos.toFixed(1)} L</TableCell>
                  <TableCell className="text-rose-700">{formatBRL(r.desconto_total_reais)}</TableCell>
                  <TableCell>{formatBRL(r.desconto_medio_aplicacao)}</TableCell>
                  <TableCell className={`font-semibold ${cor}`}>
                    {r.taxa_retorno_pct}% <span className="text-[10px] text-neutral-500">({r.retornos}/{r.clientes_unicos})</span>
                  </TableCell>
                </TableRow>
              );
            })}
            {data.items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-neutral-500 py-6">Sem dados na janela.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Funil() {
  const { unitId } = useUnit();
  const [dias, setDias] = useState(90);
  const [data, setData] = useState(null);
  const load = async () => {
    const params = new URLSearchParams({ dias: String(dias) });
    if (unitId !== "all") params.set("unit_id", unitId);
    try {
      const r = await masterApi.get(`/master/analises/funil-indicacao?${params}`);
      setData(r.data);
    } catch (e) { modal.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [unitId, dias]);
  if (!data) return <div className="text-sm text-neutral-500">Carregando...</div>;

  const roiCor = data.roi_pct >= 100 ? "emerald" : data.roi_pct >= 0 ? "amber" : "rose";

  return (
    <div className="space-y-3" data-testid="an-funil">
      <div className="card-soft rounded-xl p-3 flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Janela (dias)</label>
          <input type="number" data-testid="fu-dias" value={dias}
                 onChange={(e) => setDias(parseInt(e.target.value) || 90)}
                 className={inputCls + " mt-1 w-24"} />
        </div>
      </div>

      {/* FUNIL VISUAL */}
      <div className="card-soft rounded-xl p-5 space-y-2">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Funil de conversão</div>
        <FunnelStep label="Códigos gerados" value={data.codigos_gerados} pct={100} cor="emerald" />
        <FunnelStep label="Códigos usados" value={data.codigos_usados} pct={data.taxa_uso_pct} cor="emerald" />
        <FunnelStep label="Clientes novos cadastrados" value={data.clientes_novos_via_indicacao}
                    pct={data.codigos_gerados ? Math.round(data.clientes_novos_via_indicacao / data.codigos_gerados * 100) : 0}
                    cor="amber" />
        <FunnelStep label="Atendimentos dos indicados" value={data.atendimentos_de_indicados}
                    pct={data.codigos_usados ? Math.round(data.atendimentos_de_indicados / data.codigos_usados * 100) : 0}
                    cor="amber" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Clientes geradores" value={String(data.clientes_geradores)}
             sub="quem indicou alguém" />
        <Kpi label="Faturamento dos indicados" value={formatBRL(data.faturamento_indicados)} accent="emerald" />
        <Kpi label="Total prêmios pagos" value={formatBRL(data.premios_pagos_total)} accent="rose"
             sub={`${data.codigos_usados} × ${formatBRL(data.premio_unitario)}`} />
        <Kpi label="CAC (custo p/ cliente novo)" value={data.cac > 0 ? formatBRL(data.cac) : "—"}
             accent={data.cac < 30 ? "emerald" : "amber"} />
      </div>

      <div className="card-soft rounded-xl p-5 flex items-center justify-between flex-wrap gap-3"
           style={{ background: data.roi_pct >= 0 ? "linear-gradient(135deg,#ECFDF5 0%,#D1FAE5 100%)" : "linear-gradient(135deg,#FEF2F2 0%,#FECACA 100%)" }}>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">ROI da indicação</div>
          <div className={`font-serif-display text-3xl mt-0.5 text-${roiCor}-700`}>
            {data.roi_pct > 0 ? "+" : ""}{data.roi_pct}%
          </div>
          <div className="text-[11px] text-neutral-600">
            (Faturamento dos indicados − Prêmios pagos) ÷ Prêmios pagos
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Resultado líquido</div>
          <div className="font-serif-display text-2xl">
            {formatBRL(data.faturamento_indicados - data.premios_pagos_total)}
          </div>
        </div>
      </div>
    </div>
  );
}

function FunnelStep({ label, value, pct, cor }) {
  const corClasses = {
    emerald: "bg-emerald-100 border-emerald-300 text-emerald-900",
    amber: "bg-amber-100 border-amber-300 text-amber-900",
    rose: "bg-rose-100 border-rose-300 text-rose-900",
  }[cor] || "bg-neutral-100 border-neutral-300";
  return (
    <div className="flex items-center gap-3">
      <div className={`flex-1 px-3 py-2 rounded-lg border ${corClasses}`}
           style={{ width: `${Math.max(15, pct)}%`, maxWidth: "100%" }}>
        <div className="flex items-center justify-between gap-2">
          <span className="text-[12px] font-medium">{label}</span>
          <span className="text-[12px] font-bold">{value}</span>
        </div>
      </div>
      <div className="text-[11px] text-neutral-500 w-12 text-right">{pct}%</div>
    </div>
  );
}

function Margem() {
  const { unitId } = useUnit();
  const [dias, setDias] = useState(30);
  const [margemL, setMargemL] = useState(30);
  const [data, setData] = useState(null);
  const load = async () => {
    const params = new URLSearchParams({ dias: String(dias), margem_litro_centavos: String(margemL) });
    if (unitId !== "all") params.set("unit_id", unitId);
    try {
      const r = await masterApi.get(`/master/analises/margem-descontos?${params}`);
      setData(r.data);
    } catch (e) { modal.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [unitId, dias, margemL]);
  if (!data) return <div className="text-sm text-neutral-500">Carregando...</div>;

  return (
    <div className="space-y-3" data-testid="an-margem">
      <div className="card-soft rounded-xl p-3 flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Janela (dias)</label>
          <input type="number" data-testid="mg-dias" value={dias}
                 onChange={(e) => setDias(parseInt(e.target.value) || 30)}
                 className={inputCls + " mt-1 w-24"} />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Margem operacional (¢/L)</label>
          <input type="number" step="0.5" data-testid="mg-margem" value={margemL}
                 onChange={(e) => setMargemL(parseFloat(e.target.value) || 30)}
                 className={inputCls + " mt-1 w-24"} />
        </div>
        <div className="text-[11px] text-neutral-500 ml-auto max-w-md">
          Quanto você lucra por litro vendido <b>antes</b> dos descontos. Ajuste para refletir sua realidade (ex.: 30¢ = R$0,30/L).
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Kpi label="Baseline (sem desc.)" value={`${data.baseline.litros_medios.toFixed(1)} L/atd`}
             sub={`Ticket médio: ${formatBRL(data.baseline.ticket_medio)}`} />
        <Kpi label="Atendimentos baseline" value={String(data.baseline.qty)} />
        <Kpi label="Margem usada" value={`${data.margem_litro_centavos.toFixed(1)} ¢/L`} />
      </div>

      <div className="card-soft rounded-xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-50">
              <TableHead className="text-[10px] uppercase tracking-widest">Tipo</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Aplic.</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Litros méd/atd</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Litros adic.</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Desc. total</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Margem incr.</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Lucro liq. incr.</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">ROI incr.</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((r) => {
              const cor = r.lucro_liq_incremental >= 0 ? "text-emerald-700" : "text-rose-700";
              return (
                <TableRow key={r.tipo} data-testid={`mg-row-${r.tipo}`}>
                  <TableCell className="font-medium">{rotuloTipoDesconto(r.tipo)}</TableCell>
                  <TableCell>{r.aplicacoes}</TableCell>
                  <TableCell>{r.litros_medios_atd.toFixed(1)} L</TableCell>
                  <TableCell>{r.litros_adicionais_vs_baseline.toFixed(1)} L</TableCell>
                  <TableCell className="text-rose-700">{formatBRL(r.desconto_total)}</TableCell>
                  <TableCell className="text-emerald-700">{formatBRL(r.margem_incremental)}</TableCell>
                  <TableCell className={`font-semibold ${cor}`}>{formatBRL(r.lucro_liq_incremental)}</TableCell>
                  <TableCell className={`font-semibold ${cor}`}>
                    {r.roi_incremental_pct > 0 ? "+" : ""}{r.roi_incremental_pct}%
                  </TableCell>
                </TableRow>
              );
            })}
            {data.items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-neutral-500 py-6">Sem dados.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <div className="text-[10px] text-neutral-500 px-1">
        ROI incremental = (margem dos litros adicionais − desconto pago) ÷ desconto pago. Negativo = desconto está custando mais do que trazendo de margem.
      </div>
    </div>
  );
}
